import * as chemistryApi from '@/api/chemistry';
import * as fileApi from '@/api/file';
import { App } from 'antd';
import type { Ketcher } from 'ketcher-core';
import { Editor } from 'ketcher-react';
import 'ketcher-react/dist/index.css';
import { StandaloneStructServiceProvider } from 'ketcher-standalone';
import pathbe from 'path-browserify-esm';
import { useCallback, useEffect, useRef, useState } from 'react';
import KetcherExportDialog from './ketcherExportDialog';
import useFileStore from '@/utils/fileStore';

// Extensions that Ketcher can parse natively without conversion
const KETCHER_NATIVE_EXTENSIONS = new Set([
    '.mol', '.sdf', '.ket', '.rxn', '.smi', '.smiles', '.cml', '.rdf', '.sma',
]);

const provider = new StandaloneStructServiceProvider();

export const KetcherDisplay = ({ path }: { path?: string }) => {
    const { message } = App.useApp();
    const ketcherRef = useRef<Ketcher | null>(null);
    const [ready, setReady] = useState(false);
    const [exportDialogVisible, setExportDialogVisible] = useState(false);
    const baseRoute = import.meta.env.VITE_ROUTE_BASE ?? '';

    const openInJSMOL = useFileStore((state) => state.openInJSMOL);
    const loadMolecule = useCallback(async () => {
        if (!path || !ketcherRef.current) return;
        try {
            const ext = pathbe.extname(path).toLowerCase();
            let content: string | undefined;

            if (!KETCHER_NATIVE_EXTENSIONS.has(ext)) {
                // Non-native format: convert to SMILES via OpenBabel first
                const dir = pathbe.dirname(path);
                const basename = pathbe.basename(path, ext);
                const tempPath = `${dir}/.tmp_${basename}.smi`;

                const res = await chemistryApi.convertMolecule({
                    path,
                    to_format: 'smi',
                    output_path: tempPath,
                });

                if (res?.ok) {
                    const actualPath = res.data?.output_path ?? tempPath;
                    content = await fileApi.getRawFileResource(actualPath);
                    // Clean up temp file
                    try { await fileApi.deleteFileResource([actualPath]); } catch (_) { }
                } else {
                    message.error(res?.message ?? 'Failed to convert file for Ketcher display');
                    return;
                }
            } else {
                content = await fileApi.getRawFileResource(path);
            }

            if (content) {
                await ketcherRef.current.setMolecule(content);
            }
        } catch (error) {
            console.error('Failed to load molecule into Ketcher:', error);
        }
    }, [path]);

    const handleInit = useCallback((ketcher: Ketcher) => {
        ketcherRef.current = ketcher;
        ketcher.eventBus.on('CUSTOM_BUTTON_PRESSED', (id) => {
            if (id === 'export-to-platform') {
                setExportDialogVisible(true);
            }
        });
        ketcher.eventBus.on('CUSTOM_BUTTON_PRESSED', (id) => {
            if (id === 'open-in-jsmol' && path) {
                openInJSMOL(path);
            }
        });
        setReady(true);
    }, []);

    useEffect(() => {
        if (ready) {
            loadMolecule();
        }
    }, [ready, loadMolecule]);

    const errorHandler = useCallback((message: string) => {
        console.error('Ketcher error:', message);
    }, []);

    const customButtons = useCallback(() => {

        const items = [{
            id: 'export-to-platform',
            imageLink: baseRoute + '/icons/save.svg',
            title: 'Export'
        }];

        if (path) {
            items.push({
                id: 'open-in-jsmol',
                imageLink: baseRoute + '/icons/molecule3d.svg',
                title: 'Open in 3D Viewer',
            });
        }

        return items
    }, [baseRoute, path]);

    return (
        <div className="w-full h-full overflow-hidden">
            <KetcherExportDialog
                visible={exportDialogVisible}
                ketcher={ketcherRef.current}
                onClose={() => setExportDialogVisible(false)}
            />
            <Editor
                staticResourcesUrl={baseRoute}
                structServiceProvider={provider}
                onInit={handleInit}
                errorHandler={errorHandler}
                disableMacromoleculesEditor
                buttons={{
                    // @ts-ignore 
                    'save': { hidden: true }
                }}
                customButtons={customButtons()}
            />
        </div>
    );
};

export default KetcherDisplay;
